import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Map as MapIcon, List, X, MapPin, Maximize2, Minimize2 } from 'lucide-react';
import UnifiedMap from './UnifiedMap';
import { getImageUrl } from '../config/runtime';

/**
 * Utility: Format time ago
 */
const formatTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric'
  });
};

/**
 * DiscoveriesSection
 * 
 * Unified view of discoveries with synchronized Map + List views.
 * - Mobile: Toggle between views, bottom sheet for selection
 * - Desktop: Split view (map left, list right)
 */
const DiscoveriesSection = ({
  project,
  inventory = [],
  discoveryPins = [],
  mapBounds,
  replayPath = [],
  shouldShowTrail = false,
  messagePins = [],
  showMessagePins = false,
}) => {
  // View mode for mobile: 'map' | 'list'
  const [mobileViewMode, setMobileViewMode] = useState('map');
  
  // Selected discovery ID (synced between map and list)
  const [selectedId, setSelectedId] = useState(null);
  
  // Bottom sheet state (mobile)
  const [sheetOpen, setSheetOpen] = useState(false);
  
  // Fullscreen map state (mobile)
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  
  // Refs for scroll-to behavior
  const listRef = useRef(null);
  const itemRefs = useRef({});

  // Sort inventory reverse chronological
  const sortedInventory = useMemo(() => {
    return [...inventory].sort((a, b) => {
      const dateA = a.found_at ? new Date(a.found_at).getTime() : 0;
      const dateB = b.found_at ? new Date(b.found_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [inventory]);


  // Handle map pin click → select + scroll list into view
  const handleMapPinClick = useCallback((pin) => {
    // Pin might be a cluster with multiple items
    const items = pin.items || [];
    const firstItem = items[0];
    const itemId = firstItem?.id || pin.id;
    
    setSelectedId(itemId);
    
    // On mobile, show bottom sheet
    if (window.innerWidth < 1024) {
      setSheetOpen(true);
    } else {
      // On desktop, scroll list to item
      setTimeout(() => {
        const itemEl = itemRefs.current[itemId];
        if (itemEl) {
          itemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    }
  }, []);

  // Handle list item click → select + highlight on map
  const handleListItemClick = useCallback((itemId, item) => {
    setSelectedId(itemId);
    // On mobile list view, could optionally switch to map
    // For now, just highlight
  }, []);

  // Close sheet/fullscreen when pressing escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSheetOpen(false);
        setIsMapFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Prevent body scroll when map is fullscreen
  useEffect(() => {
    if (isMapFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMapFullscreen]);

  // Find selected item
  const selectedItem = useMemo(() => {
    if (!selectedId) return null;
    return sortedInventory.find((item, i) => {
      const itemId = item.id || `item-${i}`;
      return itemId === selectedId;
    });
  }, [sortedInventory, selectedId]);

  // Find selected pin for map highlighting
  const selectedPinId = useMemo(() => {
    if (!selectedId || !selectedItem) return null;
    // Find which pin contains this item
    for (const pin of discoveryPins) {
      if (pin.items?.some(item => item.id === selectedId || item === selectedItem)) {
        return pin.id;
      }
    }
    return null;
  }, [selectedId, selectedItem, discoveryPins]);

  const count = inventory.length;
  const hasItems = count > 0;
  const hasMap = discoveryPins.length > 0 || shouldShowTrail || mapBounds;

  // If no map data and no items, don't render
  if (!hasMap && !hasItems) {
    return null;
  }

  return (
    <section className="space-y-4">
      {/* ═══════════════════════════════════════════════════════════════
          HEADER — Title + View Toggle (mobile only)
          ═══════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          Discoveries {count > 0 && (
            <span className="text-gray-400 dark:text-gray-500 font-normal">({count})</span>
          )}
        </h3>
        
        {/* View toggle — mobile/tablet only */}
        {hasMap && hasItems && (
          <div className="lg:hidden flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setMobileViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mobileViewMode === 'map'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <MapIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Map</span>
            </button>
            <button
              onClick={() => setMobileViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mobileViewMode === 'list'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          DESKTOP: Split View (map left, list right)
          ═══════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-4">
        {/* Map Panel */}
        {hasMap && (
          <div className="space-y-2">
            <div className="relative rounded-2xl overflow-hidden h-[400px] border border-gray-200 dark:border-gray-700">
              <UnifiedMap
                mode="view"
                projects={project ? [project] : []}
                showProjectNumbers={false}
                showUserLocation={false}
                layoutContext="contained"
                replayPath={replayPath}
                bounds={mapBounds}
                hotspots={discoveryPins}
                showHotspots={true}
                messagePins={messagePins}
                showMessagePins={showMessagePins}
                onHotspotClick={handleMapPinClick}
              />
              
              {/* Selection highlight overlay on map - visual feedback */}
              {selectedPinId && (
                <div className="absolute top-3 left-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-full shadow-lg flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" />
                  {selectedItem?.name || 'Selected'}
                </div>
              )}
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 px-1">
              {count > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  {count} discoveries
                </span>
              )}
              {shouldShowTrail && (
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-0.5 bg-indigo-500 rounded" />
                  Trail
                </span>
              )}
            </div>
          </div>
        )}

        {/* List Panel */}
        {hasItems && (
          <div 
            ref={listRef}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[400px] overflow-y-auto bg-white dark:bg-gray-900"
          >
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {sortedInventory.map((item, i) => {
                const itemId = item.id || `item-${i}`;
                return (
                  <div
                    key={itemId}
                    ref={el => itemRefs.current[itemId] = el}
                  >
                    <DiscoveryRow
                      item={item}
                      isSelected={selectedId === itemId}
                      onClick={() => handleListItemClick(itemId, item)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state for list when only map */}
        {!hasItems && hasMap && (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 flex items-center justify-center h-[400px] bg-gray-50 dark:bg-gray-800/50">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <div className="text-4xl mb-2">🌿</div>
              <p className="text-sm">No discoveries yet</p>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MOBILE/TABLET: Toggle View
          ═══════════════════════════════════════════════════════════════ */}
      <div className="lg:hidden">
        {/* Map View */}
        {(mobileViewMode === 'map' || !hasItems) && hasMap && (
          <div className="space-y-2">
            {/* Fullscreen overlay - z-index must be above everything including modals */}
            {isMapFullscreen && (
              <div 
                className="fixed inset-0 bg-white dark:bg-gray-900"
                style={{ 
                  zIndex: 99999,
                  paddingTop: 'env(safe-area-inset-top, 0px)', 
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)' 
                }}
              >
                <UnifiedMap
                  mode="view"
                  projects={project ? [project] : []}
                  showProjectNumbers={false}
                  showUserLocation={false}
                  layoutContext="contained"
                  replayPath={replayPath}
                  bounds={mapBounds}
                  hotspots={discoveryPins}
                  showHotspots={true}
                  messagePins={messagePins}
                  showMessagePins={showMessagePins}
                  onHotspotClick={handleMapPinClick}
                  showFollowButton={false}
                />
                
                {/* Close fullscreen button - must be above Leaflet controls (z-1000) */}
                <button
                  onClick={() => setIsMapFullscreen(false)}
                  className="absolute top-4 right-4 p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all"
                  style={{ 
                    zIndex: 100000,
                    marginTop: 'env(safe-area-inset-top, 0px)' 
                  }}
                >
                  <Minimize2 className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                </button>
                
                {/* Legend in fullscreen - above Leaflet */}
                <div 
                  className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-200 dark:border-gray-700 shadow-lg"
                  style={{ 
                    zIndex: 100000,
                    marginBottom: 'env(safe-area-inset-bottom, 0px)' 
                  }}
                >
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300 font-medium">
                    {count > 0 && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        {count} discoveries
                      </span>
                    )}
                    {shouldShowTrail && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-4 h-0.5 bg-indigo-500 rounded" />
                        Trail
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Inline map (taller for better usability) */}
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-gray-200 dark:border-gray-700">
              <UnifiedMap
                mode="view"
                projects={project ? [project] : []}
                showProjectNumbers={false}
                showUserLocation={false}
                layoutContext="contained"
                replayPath={replayPath}
                bounds={mapBounds}
                hotspots={discoveryPins}
                showHotspots={true}
                messagePins={messagePins}
                showMessagePins={showMessagePins}
                onHotspotClick={handleMapPinClick}
                showFollowButton={false}
              />
              
              {/* Expand to fullscreen button - must be above Leaflet controls (z-1000) */}
              <button
                onClick={() => setIsMapFullscreen(true)}
                className="absolute top-3 right-3 p-2.5 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 active:scale-95 transition-all"
                style={{ zIndex: 1001 }}
                title="Expand map"
              >
                <Maximize2 className="w-4 h-4 text-gray-700 dark:text-gray-200" />
              </button>
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 px-1">
              {count > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  {count} discoveries
                </span>
              )}
              {shouldShowTrail && (
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-0.5 bg-indigo-500 rounded" />
                  Trail
                </span>
              )}
            </div>
          </div>
        )}

        {/* List View */}
        {mobileViewMode === 'list' && hasItems && (
          <div 
            ref={listRef}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-900"
          >
            {sortedInventory.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p className="text-sm">No discoveries yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {sortedInventory.map((item, i) => {
                  const itemId = item.id || `item-${i}`;
                  return (
                    <div
                      key={itemId}
                      ref={el => itemRefs.current[itemId] = el}
                    >
                      <DiscoveryRow
                        item={item}
                        isSelected={selectedId === itemId}
                        onClick={() => handleListItemClick(itemId, item)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Only list, no map */}
        {!hasMap && hasItems && (
          <div 
            className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-900"
          >
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {sortedInventory.map((item, i) => {
                const itemId = item.id || `item-${i}`;
                return (
                  <div
                    key={itemId}
                    ref={el => itemRefs.current[itemId] = el}
                  >
                    <DiscoveryRow
                      item={item}
                      isSelected={selectedId === itemId}
                      onClick={() => handleListItemClick(itemId, item)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MOBILE BOTTOM SHEET — Selected item preview
          ═══════════════════════════════════════════════════════════════ */}
      {selectedItem && sheetOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 z-40 bg-black/20"
            onClick={() => setSheetOpen(false)}
          />
          
          {/* Sheet */}
          <div 
            className="lg:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl border-t border-gray-200 dark:border-gray-700 animate-slideUp"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            
            {/* Close button */}
            <button
              onClick={() => setSheetOpen(false)}
              className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Content */}
            <div className="px-4 pb-4">
              <DiscoveryDetail item={selectedItem} />
            </div>
          </div>
        </>
      )}
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   DISCOVERY ROW — Compact, scannable, works at 500 items
   ═══════════════════════════════════════════════════════════════════════════ */
const DiscoveryRow = ({ item, isSelected, onClick }) => {
  const hasGeo = item.found_at_lat != null && item.found_at_lng != null;
  
  let timeDisplay = null;
  if (item.found_at) {
    try {
      const d = new Date(item.found_at);
      if (!isNaN(d.getTime())) {
        timeDisplay = formatTimeAgo(d);
      }
    } catch (_) {}
  }

  // Build metadata string
  const metadata = [
    item.location_note,
    item.found_by_username && `@${item.found_by_username}`,
    timeDisplay
  ].filter(Boolean).join(' · ');

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
        isSelected
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-4 border-transparent'
      }`}
    >
      {/* Thumbnail */}
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
        {item.image_url ? (
          <img
            src={getImageUrl(item.image_url, 'cardmobile')}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xl text-gray-300 dark:text-gray-600">
            🌿
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {item.name || 'Unknown'}
          </p>
          {hasGeo && (
            <MapPin className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
          )}
        </div>
        {metadata && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
            {metadata}
          </p>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
      )}
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   DISCOVERY DETAIL — Expanded view for bottom sheet
   ═══════════════════════════════════════════════════════════════════════════ */
const DiscoveryDetail = ({ item }) => {
  const hasGeo = item.found_at_lat != null && item.found_at_lng != null;
  
  let timeDisplay = null;
  if (item.found_at) {
    try {
      const d = new Date(item.found_at);
      if (!isNaN(d.getTime())) {
        timeDisplay = formatTimeAgo(d);
      }
    } catch (_) {}
  }

  return (
    <div className="flex gap-4">
      {/* Larger thumbnail */}
      <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
        {item.image_url ? (
          <img
            src={getImageUrl(item.image_url, 'cardmobile')}
            alt={item.name || 'Discovery'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-3xl text-gray-300 dark:text-gray-600">
            🌿
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          {item.name || 'Unknown Discovery'}
        </h4>
        
        {item.taxonomy?.common_name && item.taxonomy.common_name !== item.name && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {item.taxonomy.common_name}
          </p>
        )}
        
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {hasGeo && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-blue-500" />
              Location saved
            </span>
          )}
          {item.location_note && (
            <span>{item.location_note}</span>
          )}
          {item.found_by_username && (
            <span>@{item.found_by_username}</span>
          )}
          {timeDisplay && (
            <span>{timeDisplay}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscoveriesSection;

